import React from "react";
import { marked } from "marked";
import parseMD from "parse-md";
import { ImageBanner } from "components/imageBanner/imageBanner";
import { Service } from "data/DataTypes";
import "./ServiceEntryWidget.scss";
import { Tab } from "components/Tab/tab";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { faLongArrowAltRight } from "@fortawesome/fontawesome-free-solid";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Section } from "components/Section/section";

type Props = {
  caseStudyId: string;
};

type State = {
  caseStudyInfo: Service;
};

export class ServiceEntryWidget extends React.Component<Props, State> {
  componentDidUpdate(prevProps: Props) {
    if (prevProps.caseStudyId !== this.props.caseStudyId) {
      this.loadContent();
    }
  }

  componentDidMount(): void {
    this.loadContent();
  }

  loadContent() {
    const markdown = require("content/services/" +
      this.props.caseStudyId.split("./")[1]);

    fetch(markdown)
      .then((response) => {
        // fetch markdown delivers the text of the markdown
        return response.text();
      })
      .then((text) => {
        // using parse-md library to split the text from the "metadata" at top of markdown.
        let { metadata, content } = parseMD(text);

        this.setState({
          caseStudyInfo: {
            ...(metadata as Service),
            content,
            id: this.props.caseStudyId,
          },
        });

        return true;
      })
      .catch((e) => {
        // this should never happen... but you know
        console.log(e);
        return false;
      });
  }

  render() {
    if (!this.state?.caseStudyInfo) return null;

    const service = this.state.caseStudyInfo;
    if (!service) {
      return;
    }

    const markdownParsed = marked.parse(service.content);

    return (
      <div className="bg-white overflow-hidden">
        <div className="">
          <title>{service.title}</title>
          <meta name="description" content={service.eyebrow} />
        </div>

        <ImageBanner src={service.cover} title={service.eyebrow} />

        <Section>
          <div className="text-lg font-semibold uppercase tracking-wider text-blackColor">
            {service.eyebrow}
          </div>

          {(service.author || service.date || service.readingTime) && (
            <div className=" flex flex-wrap justify-start gap-32 mt-6">
              {service.author && <p>By {service.author}</p>}
              {service.date && <p>{service.date.toDateString()}</p>}
              {service.readingTime && <p>{service.readingTime}</p>}
            </div>
          )}

          {service.meta?.length! > 0 && (
            <div className="mt-8 flex flex-wrap gap-6">
              {service.meta?.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  {item.icon}
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <main className="mx-auto px-6 py-12 bg-whiteColor">
          {/* Content */}
          <article
            className="Service_root mx-auto mt-8 max-w-3xl"
            dangerouslySetInnerHTML={{
              __html: markdownParsed.toString(),
            }}
          />

          {/* CTA */}

          {service.ctaTitle && (
            <Section>
              <section className="mx-auto max-w-4xl">
                <div className="rounded-3xl bg-gray-100 p-0 sm:p-10 text-center">
                  <h2 className="text-3xl font-bold">{service.ctaTitle}</h2>
                  <p className="mt-4">{service.ctaText}</p>
                  <Tab
                    modifiers={classNames("special")}
                    label={service.ctaLabel ?? "Contact Us"}
                    to="/Contact"
                    children={
                      <FontAwesomeIcon
                        className="Header_arrowIcon"
                        icon={faLongArrowAltRight as IconProp}
                      />
                    }
                  />
                </div>
              </section>
            </Section>
          )}
        </main>
      </div>
    );
  }
}
